// Site Manager API client [FR-SITE-001]
// Uses shared API client to include X-Tenant-ID header [FR-TENANT-004]

import { api } from './client';

export interface Site {
  domain: string;
  tld: string;
  s3_bucket: string;
  s3_prefix: string;
  cf_distribution_id?: string;
  cf_domain?: string;
  acm_cert_arn?: string;
  route53_zone_id?: string;
  nameservers?: string[];
  dns_configured: boolean;
  tenant_id: string;
  status: 'pending_dns' | 'active' | 'disabled';
  generation: number;
  created_at: string;
  updated_at: string;
}

export interface SiteTicket {
  dskanban_issue_id: number;
  title: string;
  status: string;
  snapshot_s3_key?: string;
  created_at: string;
  completed_at?: string;
}

export interface SiteSnapshot {
  s3_key: string;
  size_bytes: number;
  trigger: string;
  ticket_id?: number;
  created_at: string;
  expires_at: string;
}

export interface FileInfo {
  name: string;
  is_dir: boolean;
  size?: number;
  last_modified?: string;
}

export interface FileContent {
  path: string;
  content: string;
  content_type: string;
  size: number;
}

export interface CreateSiteRequest {
  domain: string;
  s3_bucket?: string;
}

export interface CreateTicketRequest {
  title: string;
  description: string;
  attachments?: string[];
}

// Sites API
export async function listSites(): Promise<Site[]> {
  return api.get<Site[]>('/api/sites');
}

export async function getSite(domain: string): Promise<Site> {
  return api.get<Site>(`/api/sites/${domain}`);
}

export async function createSite(req: CreateSiteRequest): Promise<Site> {
  return api.post<Site>('/api/sites', req);
}

export async function deleteSite(domain: string): Promise<void> {
  return api.delete<void>(`/api/sites/${domain}`);
}

// Files API
export async function listFiles(domain: string, path = ''): Promise<FileInfo[]> {
  const url = path 
    ? `/api/sites/${domain}/files/${path}`
    : `/api/sites/${domain}/files`;
  return api.get<FileInfo[]>(url);
}

export async function getFile(domain: string, path: string): Promise<FileContent> {
  return api.get<FileContent>(`/api/sites/${domain}/files/${path}`);
}

export async function putFile(domain: string, path: string, content: string): Promise<void> {
  return api.put<void>(`/api/sites/${domain}/files/${path}`, { content });
}

export async function deleteFile(domain: string, path: string): Promise<void> {
  return api.delete<void>(`/api/sites/${domain}/files/${path}`);
}

// Tickets API
export async function listTickets(domain: string): Promise<SiteTicket[]> {
  return api.get<SiteTicket[]>(`/api/sites/${domain}/tickets`);
}

export async function createTicket(domain: string, req: CreateTicketRequest): Promise<SiteTicket> {
  return api.post<SiteTicket>(`/api/sites/${domain}/tickets`, req);
}

// Snapshots API
export async function listSnapshots(domain: string): Promise<SiteSnapshot[]> {
  return api.get<SiteSnapshot[]>(`/api/sites/${domain}/snapshots`);
}

export async function createSnapshot(domain: string): Promise<SiteSnapshot> {
  return api.post<SiteSnapshot>(`/api/sites/${domain}/snapshots`, {});
}

// Actions
export async function invalidateCache(domain: string): Promise<{ invalidation_id: string; message: string }> {
  return api.post<{ invalidation_id: string; message: string }>(`/api/sites/${domain}/invalidate`, {});
}

export async function verifyDNS(domain: string): Promise<{ verified: boolean; nameservers: string[] }> {
  return api.post<{ verified: boolean; nameservers: string[] }>(`/api/sites/${domain}/dns-verify`, {});
}

export async function restoreSnapshot(domain: string, s3Key: string): Promise<{ message: string }> {
  return api.post<{ message: string }>(`/api/sites/${domain}/snapshots/restore`, { s3_key: s3Key });
}
